import { useState } from 'react'
import MFAccordion from '../ui-components/MFAccordion'
import RenderHTML from '../ui-components/RenderHTML'
import { trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'

type FAQType = {
  title: string
  descp: string
}

const faqs = [
  {
    title: 'What is the difference between Regular and Direct funds?',
    descp: `Mutual funds offer two plans: <b>Regular</b> and <b>Direct</b>. <br/><br/>They are the <b>same fund</b>, which means the <b>fund manager, portfolio holdings, strategy,</b> and <b>risk</b> are exactly the same. The only difference is that Regular plans have a higher expense ratio (TER) - typically ~1% higher than Direct plans. Because Direct plans have a lower expense ratio, they deliver better <b>long-term returns.</b>`
  },
  {
    title: 'Will I have to pay any tax while switching from Regular to Direct funds?',
    descp: `All mutual fund investment profits are taxable in India. So you will incur capital gains tax when making a switch as per the plan in case you have made any profits.<br/><br/>The Regular to Direct plan recommendations are <b>optimized to minimise capital gains tax.</b><br/><br/>This plan is tax-efficient because we <b>only recommend switching units where LTCG is applicable</b> (or units that are <b>not in lock-in</b>), so the tax impact stays minimal. Any remaining amount that is still in <b>STCG or lock-in</b> will stay <b>under review</b>, and we'll notify you <b>when it's time to switch</b>.`
  },
  {
    title: 'Why am I seeing more than one recommendation for the same fund?',
    descp: `You may see two recommendations only when a better alternative to your current fund is available.<br/><br/>e
      <ul style="list-style-type: disc; padding-left: 20px;">
        <li><b>Power Rebalance (Recommended)</b><br/>We recommend switching to a better-performing Direct fund to help <b>improve performance</b> or <b>reduce high exposure</b> in the fund.</b></li>
        <li><b>Regular to Direct Plan</b><br/>This helps you <b>avoid paying </b>. a high expense ratio only. You will simply be recommended to switch to the Direct version of your current fund.</li>
      </ul><br/>
      In short: <b>Regular to Direct</b> helps you <b>save fees. Power Rebalance</b> helps you <b>save fees</b> and <b>upgrade to a stronger fund.</b>`
  },
  {
    title: 'How does PowerUp calculate the additional amount paid due to the high expense ratio?',
    descp: `Regular and Direct are two versions of the same fund - same portfolio, same fund manager, same strategy.</br></br>
    The only difference is that Regular funds have a higher expense ratio (~1%) than Direct funds, as it includes intermediary fees. This isn’t charged separately, but built into the fund’s NAV.</br></br>
    We compare how your investment has grown in the Regular fund versus how it would have grown in the Direct version of the same fund.</br></br>
    The <b>difference between the two values</b> s shown as the additional amount you’ve paid by staying invested in the Regular fund.`
  },
  {
    title: 'Will I have to switch the full amount or can I do it partially?',
    descp: `It can be <b>full or partial</b> — depending on which units qualify for <b>LTCG</b> (and are not in lock-in).<br/><br/>We'll recommend switching <b>only the tax-efficient amount</b>. The remaining amount will stay <b>under review</b>, and we'll notify you when it's time.`
  },
  {
    title: 'How frequently does this plan get updated?',
    descp: `This plan updates based on your <b>latest portfolio data</b>.<br/><br/>Whenever you make <b>any new transaction</b> (investment, withdrawal, switch, etc.), <b>refetch</b> your portfolio so the plan stays accurate and up to date.`
  },
  {
    title: 'What if I have an ELSS lock-in?',
    descp: `ELSS funds come with a <b>3-year lock-in</b>, so locked units can't be switched yet.<br/><br/>We only recommend switching the amount that is <b>not in lock-in</b> and qualifies for <b>LTCG</b>. The rest stays <b>under review</b>, and we'll notify you when it becomes eligible.`
  },
  {
    title: 'Why are there no recommendations for some of my funds?',
    descp: `We only recommend a Regular → Direct switch when it's <b>tax-efficient</b> for you.<br/><br/>Some funds may not show a recommendation yet because:<br/>
      <ul style="list-style-type: disc; padding-left: 20px;">
        <li><b>All units are under STCG:</b> Switching now would attract higher short-term capital gains tax, so we keep it <b>under review</b>.</li>
        <li><b>ELSS lock-in:</b> Locked ELSS units can't be switched yet. We'll recommend it once they're eligible.</li>
        <li><b>IDCW funds:</b> We currently don't provide Regular to Direct recommendations for IDCW plans yet.</li>
      </ul><br/>
      These funds will be <b>under review</b>, and we'll update your plan as soon as switching becomes possible and tax-efficient.`
  },
  {
    title: 'Why should I trust the recommendations made?',
    descp: `We are a SEBI-registered RIA (Registered Investment Advisor), which means our advice is unbiased and built entirely around what's best for the user.<br/><br/>Our recommendations are built by <b>inhouse experts and fund analysts</b> on 20+ years of data and <b>backtested to deliver superior returns.</b><br/><br/>Note: Past success doesn't guarantee future returns and neither do we. But, we can assure you that our experts and portfolio review engine are always on top of your portfolio, doing all the heavy lifting so you don't have to.`
  }
] as FAQType[]

interface RegularToDirectPlanFAQsProps {
  faqHeader?: string
  screenType: string
}

export const RegularToDirectPlanFAQs = ({ faqHeader = 'FAQs', screenType }: RegularToDirectPlanFAQsProps) => {
  const [accordionState, setAccordionState] = useState<{ [key: string]: boolean }>({})
  return (
    <div className='bg-grey80 px-5 pt-5'>
      {faqHeader && <div className='text-[#6A6E6E] text-[13px] pb-2 pl-4'>{faqHeader}</div>}
      <div className='rounded-[12px] flex flex-col gap-[1px] overflow-hidden'>
        {faqs?.map(({ title, descp }, index) => {
          return (
            <div key={`${title}${index}`} className='bg-white'>
              <MFAccordion
                title={title}
                accordionHeaderClassName='!px-4 !min-h-12'
                accordionBodyClassName='!px-4 max-w-[90%]'
                isExpanded={!!accordionState?.[title]}
                onAccordionClick={() => {
                  setAccordionState((accordionStatePrev) => {
                    trackEvent({
                      eventName: EventNames.UserClicked,
                      payload: {
                        eventId: title,
                        responseName: accordionStatePrev?.[title] ? 'Collapse' : 'Expand',
                        ScreenName: 'FAQ',
                        screenType: screenType
                      }
                    })
                    return {
                      ...accordionStatePrev,
                      [title]: !accordionStatePrev?.[title]
                    }
                  })
                }}
              >
                <div className='text-[#6A6E6E] text-[13px] leading-[150%] pb-4'>
                  <RenderHTML innerHTMLText={descp} />{' '}
                </div>
              </MFAccordion>
            </div>
          )
        })}
      </div>
    </div>
  )
}
